import { useCallback, useEffect, useState } from "react";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import {
  DEFAULT_CONSTRAINT_VISIBILITY_UI_STATE,
  getConstraintVisibilityUiState,
  setConstraintVisibilityUiState,
  type ConstraintVisibilityUiState,
} from "./visibilityEvents";

export type ConstraintVisibilityState = ConstraintVisibilityUiState;

const defaultState = (): ConstraintVisibilityState => ({
  ...DEFAULT_CONSTRAINT_VISIBILITY_UI_STATE,
});

/** Persists preferences when switching equality ↔ tendon (separate inspector instances mount/unmount). */
let rememberedConstraintVisibilityState: ConstraintVisibilityState = defaultState();

export type UseConstraintVisibilityControlsArgs = {
  equalityUuid?: string | null;
  tendonUuid?: string | null;
  enabled?: boolean;
};

export type UseConstraintVisibilityControlsResult = {
  state: ConstraintVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  reset: () => void;
};

export function useConstraintVisibilityControls({
  equalityUuid = null,
  tendonUuid = null,
  enabled = true,
}: UseConstraintVisibilityControlsArgs): UseConstraintVisibilityControlsResult {
  const showConstraintMarkers = useViewerStore((s) => s.showConstraintMarkers);
  const viewerActions = useViewerActions();
  const [state, setStateInternal] = useState<ConstraintVisibilityState>(() => ({
    ...defaultState(),
    ...rememberedConstraintVisibilityState,
    constraintsVisible: getConstraintVisibilityUiState().constraintsVisible,
    tendonsVisible: getConstraintVisibilityUiState().tendonsVisible,
    labelsOff: getConstraintVisibilityUiState().labelsOff,
    tendonLabelsOff: getConstraintVisibilityUiState().tendonLabelsOff,
  }));

  const hasInspectorTarget =
    (equalityUuid != null && String(equalityUuid).length > 0) ||
    (tendonUuid != null && String(tendonUuid).length > 0);
  const disabled = !enabled || !hasInspectorTarget;
  const focusDisabled = disabled;

  const setState = useCallback((patch: Partial<ConstraintVisibilityState>) => {
    setStateInternal((prev) => {
      const next = { ...prev, ...patch };
      rememberedConstraintVisibilityState = next;
      return next;
    });
  }, []);

  const ensureConstraintMarkersVisible = useCallback(() => {
    if (!showConstraintMarkers) {
      viewerActions.toggleShowConstraintMarkers();
    }
  }, [showConstraintMarkers, viewerActions]);

  useEffect(() => {
    setConstraintVisibilityUiState({
      focus: state.focus,
    });
  }, [state.focus]);

  useEffect(() => {
    return () => {
      setConstraintVisibilityUiState({ focus: false });
    };
  }, []);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) ensureConstraintMarkersVisible();
    setState({ focus: next });
  }, [focusDisabled, ensureConstraintMarkersVisible, setState, state.focus]);

  const reset = useCallback(() => {
    const next = defaultState();
    rememberedConstraintVisibilityState = next;
    setConstraintVisibilityUiState(DEFAULT_CONSTRAINT_VISIBILITY_UI_STATE);
    setStateInternal(next);
  }, []);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    reset,
  };
}
